/** Map an evidence-room sidebar tab to its help topic. */
export function sedrTabHelpTopic(
  tab: 'dashboard' | 'phases' | 'compliance' | 'evidence' | 'access' | 'reports' | 'audit' | 'settings',
  options?: { setupIncomplete?: boolean; guest?: boolean },
): string {
  if (options?.guest) {
    switch (tab) {
      case 'dashboard':
        return 'guest-sedr-dashboard';
      case 'compliance':
        return 'guest-sedr-requirements';
      case 'evidence':
        return 'guest-sedr-evidence';
      case 'audit':
        return 'guest-sedr-audit';
      default:
        return 'guest-sedr';
    }
  }
  if (options?.setupIncomplete) return 'sedr-setup';
  switch (tab) {
    case 'dashboard':
      return 'sedr-dashboard';
    case 'phases':
      return 'sedr-phases';
    case 'compliance':
      return 'sedr-requirements';
    case 'evidence':
      return 'sedr-evidence';
    case 'access':
      return 'sedr-access';
    case 'reports':
      return 'sedr-scheduled-report';
    case 'audit':
      return 'sedr-audit';
    case 'settings':
      return 'sedr-settings';
    default:
      return 'sedr';
  }
}

/** Map a user Settings sidebar section to its help subsection id. */
export function settingsSectionHelpTopic(
  section: 'passphrase' | 'recovery' | 'webauthn' | 'notifications' | null | undefined,
): string {
  switch (section) {
    case 'passphrase':
      return 'settings-passphrase';
    case 'recovery':
      return 'settings-recovery';
    case 'webauthn':
      return 'settings-passkey';
    case 'notifications':
      return 'settings-notifications';
    default:
      return 'settings';
  }
}

/** Resolve the default help topic from a pathname (and optional page context). */
export function defaultTopicFromPath(path: string, context?: string | null): string {
  const normalized = path.replace(/\/+$/, '') || '/';
  // Explicit page context wins (vault, recovery, active SEDR tab, …).
  if (context) return context;

  if (/^\/i\/[^/]+$/.test(normalized)) return 'guest-information';
  if (/^\/f\/[^/]+\/g\/[^/]+$/.test(normalized)) return 'guest-files';
  if (normalized === '/share') return 'guest-quick-share';
  if (/^\/s\/[^/]+\/g\/[^/]+$/.test(normalized)) return 'guest-sedr';
  if (normalized === '/guest/settings' || normalized.startsWith('/guest/settings/')) {
    const match = normalized.match(/^\/guest\/settings\/([^/]+)$/);
    if (match) {
      const segment = match[1];
      if (segment === 'passphrase') return 'guest-settings-passphrase';
      if (segment === 'recovery') return 'guest-settings-recovery';
      if (segment === 'notifications') return 'guest-settings-notifications';
      if (segment === 'totp') return 'guest-settings-totp';
    }
    return 'guest-settings';
  }
  if (normalized === '/guest' || normalized.startsWith('/guest/')) return 'guest-sedr-dashboard';

  if (normalized === '/information' || normalized.startsWith('/information/')) return 'information';
  if (normalized === '/files' || normalized.startsWith('/files/')) return 'files';
  if (normalized === '/sedr/library' || normalized.startsWith('/sedr/library/')) return 'sedr-library';
  if (normalized === '/sedr' || normalized.startsWith('/sedr/')) return 'sedr';
  if (normalized === '/sms') return 'sms';
  if (normalized === '/admin' || normalized.startsWith('/admin/')) {
    if (normalized === '/admin/password-policies') return 'admin-password-policies';
    if (normalized === '/admin/settings') return 'admin-settings';
    if (normalized === '/admin/disk') return 'admin-disk';
    if (normalized === '/admin/reply-backlog') return 'admin-backlog';
    if (normalized === '/admin/audit') return 'admin-audit';
    if (normalized === '/admin/recovery') return 'admin-recovery';
    if (normalized === '/admin/emergency-revoke') return 'admin-emergency-revoke';
    return 'admin';
  }
  if (normalized === '/settings' || normalized.startsWith('/settings/')) {
    const match = normalized.match(/^\/settings\/([^/]+)$/);
    if (match) {
      const segment = match[1];
      if (
        segment === 'passphrase'
        || segment === 'recovery'
        || segment === 'webauthn'
        || segment === 'notifications'
      ) {
        return settingsSectionHelpTopic(segment);
      }
    }
    return 'settings';
  }
  if (normalized === '/help') return 'getting-started';

  return 'getting-started';
}

export function pageHelpContext(): string | null {
  const el = document.querySelector('[data-help-context]');
  return el?.getAttribute('data-help-context') ?? null;
}
